import { MiniKit } from '@worldcoin/minikit-js';

const WORLD_CHAIN_ID = 480;
const USER_OPERATION_POLL_INTERVAL_MS = 1_500;
const USER_OPERATION_TIMEOUT_MS = 120_000;

export type WorldCalldataTransaction = {
  to: string;
  data: string;
  value?: string;
};

type UserOperationStatus = {
  status: 'pending' | 'success' | 'failed';
  userOpHash: string;
  transaction_hash: string | null;
  sender: string | null;
  nonce: string | null;
};

export type WorldTransactionErrorCode = 'transaction_cancelled' | 'transaction_failed';

export type WorldTransactionNotice = {
  title: string;
  description: string;
};

const WORLD_TRANSACTION_NOTICE_EVENT = 'bubble-wallet:world-transaction-notice';

function dispatchWorldTransactionNotice(notice: WorldTransactionNotice | null): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent<WorldTransactionNotice | null>(WORLD_TRANSACTION_NOTICE_EVENT, {
    detail: notice,
  }));
}

async function waitForTransactionNoticePaint(): Promise<void> {
  if (typeof window === 'undefined') return;
  await new Promise<void>((resolve) => window.requestAnimationFrame(() => resolve()));
}

export function subscribeWorldTransactionNotice(
  listener: (notice: WorldTransactionNotice | null) => void,
): () => void {
  if (typeof window === 'undefined') return () => undefined;
  const handleNotice = (event: Event) => {
    listener((event as CustomEvent<WorldTransactionNotice | null>).detail ?? null);
  };
  window.addEventListener(WORLD_TRANSACTION_NOTICE_EVENT, handleNotice);
  return () => window.removeEventListener(WORLD_TRANSACTION_NOTICE_EVENT, handleNotice);
}

export class WorldTransactionError extends Error {
  readonly code: WorldTransactionErrorCode;

  constructor(code: WorldTransactionErrorCode, message: string) {
    super(message);
    this.name = 'WorldTransactionError';
    this.code = code;
  }
}

function formatErrorDetails(details: unknown): string | null {
  if (typeof details === 'string' && details.trim()) return details;
  if (details && typeof details === 'object') {
    try {
      return JSON.stringify(details);
    } catch {
      return null;
    }
  }
  return null;
}

export function getWorldMiniKitErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) {
    const candidate = error as Error & {
      code?: unknown;
      error_code?: unknown;
      error_message?: unknown;
      details?: unknown;
    };
    const code = typeof candidate.code === 'string'
      ? candidate.code
      : typeof candidate.error_code === 'string'
        ? candidate.error_code
        : null;
    const detail = typeof candidate.error_message === 'string' && candidate.error_message
      ? candidate.error_message
      : formatErrorDetails(candidate.details);

    if (code && detail) {
      return `World App 交易失敗（${code}）：${detail}`;
    }
    return candidate.message;
  }

  if (typeof error === 'object' && error !== null) {
    const candidate = error as {
      code?: unknown;
      error_code?: unknown;
      error_message?: unknown;
      details?: unknown;
      detail?: unknown;
    };
    const code = typeof candidate.code === 'string'
      ? candidate.code
      : typeof candidate.error_code === 'string'
        ? candidate.error_code
        : null;
    const detail = typeof candidate.error_message === 'string' && candidate.error_message
      ? candidate.error_message
      : typeof candidate.detail === 'string' && candidate.detail
        ? candidate.detail
        : formatErrorDetails(candidate.details);

    if (code && detail) return `World App 交易失敗（${code}）：${detail}`;
    if (code) return `World App 交易失敗（${code}）。`;
  }

  return 'World App 沒有完成這筆交易。';
}

async function readUserOperationStatus(userOpHash: string): Promise<UserOperationStatus> {
  const response = await fetch(
    `https://developer.world.org/api/v2/minikit/userop/${encodeURIComponent(userOpHash)}`,
    { headers: { Accept: 'application/json' } },
  );

  let payload: unknown = null;
  try {
    payload = await response.json();
  } catch {
    // The status endpoint should return JSON; preserve a useful error if it does not.
  }

  if (!response.ok) {
    throw new Error(getWorldMiniKitErrorMessage(payload));
  }

  if (
    typeof payload !== 'object' || payload === null ||
    !('status' in payload) ||
    !(['pending', 'success', 'failed'] as const).includes(payload.status as UserOperationStatus['status'])
  ) {
    throw new Error('World App 回傳了無法辨識的 User Operation 狀態。');
  }

  return payload as UserOperationStatus;
}

async function resolveUserOperationTransactionHash(userOpHash: string): Promise<string> {
  const timeoutAt = Date.now() + USER_OPERATION_TIMEOUT_MS;

  while (Date.now() < timeoutAt) {
    const status = await readUserOperationStatus(userOpHash);
    if (status.status === 'success' && status.transaction_hash) {
      return status.transaction_hash;
    }
    if (status.status === 'failed') {
      throw new WorldTransactionError(
        'transaction_failed',
        `World App 交易已提交，但鏈上執行失敗（User Operation：${userOpHash}）。`,
      );
    }

    await new Promise((resolve) => window.setTimeout(resolve, USER_OPERATION_POLL_INTERVAL_MS));
  }

  throw new WorldTransactionError(
    'transaction_failed',
    `World App User Operation 尚未確認（${userOpHash}），請稍後查看交易狀態。`,
  );
}

function isTransactionCancellation(error: unknown): boolean {
  const candidate = error as {
    code?: unknown;
    error_code?: unknown;
    error_message?: unknown;
  } | null;
  const code = typeof candidate?.code === 'string'
    ? candidate.code
    : typeof candidate?.error_code === 'string'
      ? candidate.error_code
      : '';
  const message = typeof candidate?.error_message === 'string'
    ? candidate.error_message
    : error instanceof Error ? error.message : '';
  return /cancel|reject|user_rejected/i.test(`${code} ${message}`);
}

export async function sendWorldTransaction(
  transactions: WorldCalldataTransaction[],
  onSubmitted?: (userOpHash: string) => void,
  notice?: WorldTransactionNotice,
): Promise<{ userOpHash: string; txHash: string }> {
  if (!MiniKit.isInstalled()) {
    throw new WorldTransactionError(
      'transaction_failed',
      '請在 World App 內開啟 Bubble Wallet 後再進行交易。',
    );
  }

  dispatchWorldTransactionNotice(notice ?? null);
  if (notice) await waitForTransactionNoticePaint();
  try {
    const result = await MiniKit.sendTransaction({
      chainId: WORLD_CHAIN_ID,
      transactions,
    });

    if (result.executedWith !== 'minikit') {
      throw new Error('World App 沒有使用原生 MiniKit 完成交易。');
    }

    const userOpHash = result.data.userOpHash;
    if (typeof userOpHash !== 'string' || !userOpHash) {
      throw new Error('World App 沒有回傳 User Operation 編號。');
    }

    onSubmitted?.(userOpHash);
    const txHash = await resolveUserOperationTransactionHash(userOpHash);
    return { userOpHash, txHash };
  } catch (error) {
    if (error instanceof WorldTransactionError) throw error;
    throw new WorldTransactionError(
      isTransactionCancellation(error) ? 'transaction_cancelled' : 'transaction_failed',
      getWorldMiniKitErrorMessage(error),
    );
  } finally {
    dispatchWorldTransactionNotice(null);
  }
}
